import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { ACTION_NAMES, Automation, ELEMENT_TYPES, NewButton, Tooltip, getElementName, getFlowElement } from 'prace-common-components'
import { $Container, $List, $ModalContainer, $Paragraph, $Description, $LinkItem, $NavItem } from './styles'
import { DeleteElementModalProps } from './types'
import Grid from '@material-ui/core/Grid'
import { conditionText } from 'util/conditionUtils'
import { getActionSubTitle } from 'util/getActionSubTitle'
import { Routes } from 'constants/global'
import { collectElementIds, getElementUsage } from 'util/getElementUsage'
import { RootState } from 'store'
import { useAppDispatch, useAppSelector } from 'store/hooks'

export const DeleteElementModal: React.FC<DeleteElementModalProps> = ({
  call,
  elements,
  description,
  handleDelete,
  handleCancel,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const storeTemplates = useAppSelector((state: RootState) => state.calls.templates) || []
  const {
    automations,
    conditions,
    templates,
  } = getElementUsage(elements.flatMap(collectElementIds), call, storeTemplates)

  const enableDelete = !(automations.length || conditions.length || templates.length)
  /* An automation can belong to any step, so the event naming is resolved against its own step */
  const isFirstStep = (automation: Automation) => call.flow[0]?.id === automation.parentId
  const plural = elements.length > 1
  const subject = plural ? 'these elements' : 'this element'

  /* Select the blocking element in the editor so the user can go straight to the conflict */
  const goToElement = (ownerId: number) => {
    const { step, form, element, groupElement } = getFlowElement(call, ownerId)
    if(!step) return
    /* The editor selects by array index, which order does not always match:
       removeHiddenElements drops hidden elements without renumbering the rest */
    const select = (order: number, type: ELEMENT_TYPES) => dispatch({ type: 'selectElement', payload: { order, type }})
    select(call.flow.findIndex((s) => s.id === step.id), ELEMENT_TYPES.STEP)
    if(form) select(step.flow.findIndex((f) => f.id === form.id), ELEMENT_TYPES.FORM)
    /* Children of a group are not selectable on their own, the group is */
    const formElement = groupElement || element
    if(form && formElement) select(form.flow.findIndex((e) => e.id === formElement.id), ELEMENT_TYPES.ELEMENT)
    handleCancel()
    /* The flow only renders the element after the selection re-render,
       and closing the modal restores the page scroll */
    if(formElement)
      setTimeout(() => document.getElementById(`flow-element-${formElement.id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0)
  }

  /* The call editor already selects the step and opens the actions tab for this query param */
  const goToAutomation = (automationId: number) => {
    handleCancel()
    navigate(`${Routes.CALL(call.id)}?automation=${automationId}`, { replace: true })
  }

  return (
    <$ModalContainer>
      <$Description>{enableDelete ? `There are no automations, conditions or email templates using ${subject}.` : `You need to stop using ${subject} on the automations, conditions and email templates below before deleting ${plural ? 'them' : 'it'}.`}</$Description>
      {enableDelete && !!description && <$Description>{description}</$Description>}
      <Grid container>
        {!!(automations.length || conditions.length) && <Grid item xs={6}>
          {!!automations.length &&
          <>
            <$Paragraph>There are automations using {subject}:</$Paragraph>
            <$List>
              {automations.map((automation) =>
                <li key={automation.id}>
                  <$NavItem onClick={() => goToAutomation(automation.id)}>
                    {`${ACTION_NAMES[automation.type]}: ${getActionSubTitle(automation, storeTemplates, call, isFirstStep(automation))}`}
                  </$NavItem>
                </li>,
              )}
            </$List>
          </>}
          {!!conditions.length && <>
            <$Paragraph>These elements have conditions using {subject}:</$Paragraph>
            <$List>
              {conditions.map((condition) =>
                <Tooltip
                  placement='top'
                  key={condition.id}
                  show
                  text={conditionText(call, condition)}
                >
                  <li>
                    <$NavItem onClick={() => goToElement(condition.ownerId)}>
                      {getElementName(condition.ownerId, call)}
                    </$NavItem>
                  </li>
                </Tooltip>,
              )}
            </$List>
          </>
          }
        </Grid>}
        {!!templates.length && <Grid item xs={6}>
          <$Paragraph>There are email templates using {subject}:</$Paragraph>
          <$List>
            {templates.map((template) =>
              <li key={template.id}>
                <$LinkItem target='_blank' rel='noopener noreferrer' href={Routes.TEMPLATE(template.id)} >{template.title}</$LinkItem>
              </li>,
            )}
          </$List>
        </Grid>}
      </Grid>
      <$Container container justifyContent='space-between'>
        <Grid item xs container justifyContent='flex-start'>
          <NewButton variant='outlined' onClick={handleCancel}>Cancel</NewButton>
        </Grid>
        <Grid item xs={3} container justifyContent='flex-end'>
          <NewButton disabled={!enableDelete} onClick={handleDelete}>
            Delete
          </NewButton>
        </Grid>
      </$Container>
    </$ModalContainer>
  )
}
